import { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { motion, AnimatePresence, MotionConfig, useReducedMotion, useScroll, useTransform } from 'motion/react';
import { ArrowRight, Facebook, Instagram, ChevronUp, Pause, Play } from 'lucide-react';
import CubeLogo from './components/CubeLogo';
import ContactForm from './components/ContactForm';
import ConsentManager from './components/ConsentManager';
import OfferSummary from './components/OfferSummary';
import FaqSection from './components/FaqSection';
import { CONTACT_EMAIL } from './lib/site';
import { getCloudinaryProps, getOptimizedAssetUrl } from './lib/cloudinary';

// Rarely opened, so it's excluded from the main bundle and fetched on demand.
const ContactModal = lazy(() => import('./components/ContactModal'));

const heroImages = [
  "https://res.cloudinary.com/dwoegbacd/image/upload/v1781784227/WONINGEN_-_BLUEPRINT_01_final_bvgudb.webp",
  "https://res.cloudinary.com/dwoegbacd/image/upload/v1781784227/WONINGEN_-_BLUEPRINT_02_final_qviocu.webp",
  "https://res.cloudinary.com/dwoegbacd/image/upload/v1781784227/WONINGEN_-_BLUEPRINT_03_final_yb4pol.webp"
];

const Logo = ({ className = "" }: { className?: string }) => (
  <div className={`flex items-center ${className}`}>
    <CubeLogo />
  </div>
);

const HERO_WIDTHS = [640, 1024, 1600, 2000];
const heroImageProps = (index: number) => getCloudinaryProps(heroImages[index], HERO_WIDTHS, 1600, "100vw");

const titleVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.2,
    }
  },
  exit: {
    opacity: 0,
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  }
};

const letterVariants = {
  hidden: { y: "100%", opacity: 0 },
  visible: { 
    y: "0%", 
    opacity: 1,
    transition: {
      duration: 1.2,
      ease: [0.16, 1, 0.3, 1] as const
    }
  },
  exit: {
    y: "-100%",
    opacity: 0,
    transition: { duration: 0.8, ease: "easeInOut" as const }
  }
};

export default function App() {
  const [currentSlide, setCurrentSlide] = useState(0);
  // Until the first slide change the hero renders in its final state, so the
  // prerendered HTML is visible immediately instead of waiting for hydration.
  const [hasRotated, setHasRotated] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [userPausedSlideshow, setUserPausedSlideshow] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const slideshowPaused = userPausedSlideshow || !!prefersReducedMotion;

  const containerRef = useRef(null);
  const { scrollY } = useScroll();
  
  // Parallax and fade effects for hero content
  const heroY = useTransform(scrollY, [0, 600], [0, -100]);
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.1]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (slideshowPaused) return;
    const timer = setInterval(() => {
      setHasRotated(true);
      setCurrentSlide((prev) => (prev + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [slideshowPaused]);

  return (
    <MotionConfig reducedMotion="user">
    <div id="top" ref={containerRef} className="min-h-screen bg-surface text-text-main font-sans selection:bg-primary selection:text-white overflow-x-hidden">
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-[120] focus:bg-surface focus:text-text-main focus:px-4 focus:py-3 focus:text-sm focus:font-semibold"
      >
        Ga naar inhoud
      </a>
      <ConsentManager />
      {/* Navigation */}
      <nav aria-label="Hoofdmenu" className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 flex justify-between items-center pointer-events-none ${
        isScrolled
          ? 'bg-surface/95 backdrop-blur-md text-text-main shadow-sm py-3 px-6 md:px-12'
          : 'on-dark p-6 md:p-12 text-white bg-gradient-to-b from-text-main/70 to-transparent'
      }`}>
        <a
          href="#top"
          aria-label="Project LEO, naar boven"
          className={`pointer-events-auto block transition-transform duration-500 ${isScrolled ? 'scale-75 origin-left' : 'scale-100'}`}
        >
          <Logo />
        </a>
        <div className={`hidden md:flex gap-12 text-xs tracking-[0.3em] uppercase font-medium pointer-events-auto transition-all duration-500 ${isScrolled ? 'gap-8' : 'gap-12'}`}>
          <a href="#concept" className="hover:text-primary transition-colors">Concept</a>
          <a href="#aanbod" className="hover:text-primary transition-colors">Aanbod</a>
          <a href="#locatie" className="hover:text-primary transition-colors">Locatie</a>
          <a href="#faq" className="hover:text-primary transition-colors">FAQ</a>
          <a href="#contact" className="hover:text-primary transition-colors">Contact</a>
        </div>
      </nav>

      {/* Hero Section */}
      <main id="main" tabIndex={-1} className="focus:outline-none">
      <section className="on-dark relative h-screen w-full flex items-center justify-center overflow-hidden bg-text-main">
        <div className="absolute inset-0 z-0 hero-fade">
          <AnimatePresence mode="wait">
            <motion.div 
              key={currentSlide}
              className="absolute inset-0 w-full h-full"
              initial={hasRotated ? { opacity: 0, scale: 1.1 } : false}
              animate={{ opacity: 0.8, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 2, ease: [0.4, 0, 0.2, 1] }}
            >
              <motion.img 
                style={{ scale: heroScale }}
                {...heroImageProps(currentSlide)}
                alt={`LEO Exterieur ${currentSlide + 1}`}
                width={1600}
                height={1000}
                fetchPriority={currentSlide === 0 ? 'high' : undefined}
                className="w-full h-full object-cover"
                referrerPolicy="no-referrer"
              />
            </motion.div>
          </AnimatePresence>
          <div className="absolute inset-0 bg-text-main/30 z-10"></div>
        </div>

        {/* Slide Switcher */}
        <div className="absolute bottom-28 md:bottom-12 left-6 md:left-12 z-20 flex items-end gap-6 md:gap-8">
          {heroImages.map((_, index) => (
            <button
              key={index}
              onClick={() => { setHasRotated(true); setCurrentSlide(index); }}
              className="group flex flex-col gap-3 items-start py-2"
              aria-label={`Toon afbeelding ${index + 1}`}
              aria-current={currentSlide === index}
            >
              <span className={`text-xs tracking-widest transition-colors duration-500 [text-shadow:0_1px_3px_rgba(0,0,0,0.6)] ${currentSlide === index ? 'text-white' : 'text-white/75 group-hover:text-white'}`}>
                0{index + 1}
              </span>
              <div className="w-12 h-[2px] bg-white/30 relative overflow-hidden">
                <motion.div
                  className="absolute top-0 left-0 h-full bg-primary"
                  initial={{ width: "0%" }}
                  animate={{ width: currentSlide === index ? "100%" : "0%" }}
                  transition={{ duration: currentSlide === index && !slideshowPaused ? 6 : 0.3, ease: "linear" }}
                />
              </div>
            </button>
          ))}
          <button
            onClick={() => setUserPausedSlideshow((p) => !p)}
            className="mb-[-2px] p-2 text-white/90 hover:text-white transition-colors"
            aria-pressed={slideshowPaused}
            aria-label={slideshowPaused ? 'Slideshow hervatten' : 'Slideshow pauzeren'}
          >
            {slideshowPaused ? <Play size={16} /> : <Pause size={16} />}
          </button>
        </div>
        
        <motion.div 
          style={{ y: heroY, opacity: heroOpacity }}
          className="z-10 text-center text-surface w-full px-4 hero-enter"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={hasRotated ? "hidden" : false}
              animate="visible"
              exit="exit"
              variants={titleVariants}
            >
              <motion.h1 
                className="font-serif text-7xl md:text-[12vw] leading-none tracking-[0.15em] uppercase flex justify-center ml-[0.15em]"
              >
                <span className="sr-only">Project LEO: nieuwbouwwoningen te koop in Grobbendonk | Blueprint Development</span>
                {["L", "E", "O"].map((letter, i) => (
                  <span key={i} className="block overflow-hidden pb-4 px-1 md:px-2" aria-hidden="true">
                    <motion.span variants={letterVariants} className="block">
                      {letter}
                    </motion.span>
                  </span>
                ))}
              </motion.h1>
              <motion.p 
                initial={hasRotated ? { opacity: 0, y: 20 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.6 }}
                className="mt-8 text-lg md:text-xl font-normal tracking-[0.4em] uppercase [text-shadow:0_1px_8px_rgba(0,0,0,0.5)]"
              >
                Leopoldstraat, Grobbendonk
              </motion.p>
              <motion.p
                initial={hasRotated ? { opacity: 0, y: 20 } : false}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, delay: 0.8 }}
                className="mt-4 text-sm md:text-base font-normal tracking-[0.3em] uppercase text-white/90 [text-shadow:0_1px_8px_rgba(0,0,0,0.5)]"
              >
                8 exclusieve nieuwbouwwoningen
              </motion.p>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        {/* Scroll Indicator */}
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 z-20 hidden md:block"
        >
          <div className="w-[1px] h-16 bg-gradient-to-b from-surface to-transparent opacity-50"></div>
        </motion.div>

        {/* 6% btw sticker */}
        <motion.a
          href="#aanbod"
          initial={{ opacity: 0, scale: 0.6, rotate: -18 }}
          animate={{ opacity: 1, scale: 1, rotate: -8 }}
          transition={{ duration: 0.8, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          whileHover={{ rotate: 0, scale: 1.05 }}
          className="absolute top-28 right-6 md:top-36 md:right-12 z-20 flex flex-col items-center justify-center w-24 h-24 md:w-28 md:h-28 rounded-full bg-primary text-white border-2 border-white/50 shadow-2xl text-center"
          aria-label="6% btw mogelijk, onder voorwaarden. Bekijk het aanbod."
        >
          <span className="font-serif text-3xl md:text-4xl leading-none">6%</span>
          <span className="mt-1 text-[9px] md:text-[10px] tracking-[0.15em] uppercase font-semibold leading-tight">
            btw
            <br />
            mogelijk*
          </span>
        </motion.a>

        {/* Floating Contact CTA */}
        <motion.button
          onClick={() => setShowContactModal(true)}
          initial={{ opacity: 0, y: 20 }}
          animate={{
            opacity: 1,
            y: 0,
            ...(prefersReducedMotion
              ? {}
              : {
                  boxShadow: [
                    '0 0 0 0 rgba(136, 69, 48, 0.6)',
                    '0 0 0 16px rgba(136, 69, 48, 0)',
                    '0 0 0 0 rgba(136, 69, 48, 0)'
                  ]
                })
          }}
          transition={{
            opacity: { duration: 0.8, delay: 1 },
            y: { duration: 0.8, delay: 1 },
            boxShadow: { duration: 2.5, repeat: Infinity, ease: 'easeOut', delay: 1.5 }
          }}
          whileHover={{ scale: 1.05 }}
          className="hidden md:flex absolute bottom-12 right-12 z-20 items-center gap-3 bg-primary text-white px-8 py-5 text-sm tracking-[0.25em] uppercase font-bold shadow-2xl hover:bg-text-main transition-colors"
        >
          Neem contact op <ArrowRight size={16} />
        </motion.button>
      </section>

      {/* Concept Section */}
      <section id="concept" className="relative py-32 px-6 md:px-12 max-w-7xl mx-auto overflow-hidden">
        <div className="absolute inset-0 lamellen-overlay opacity-50 pointer-events-none"></div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-24 items-center relative z-10">
          <div className="md:col-span-5">
            <h2 className="font-serif text-4xl md:text-6xl tracking-wide leading-tight mb-8">
              Twee levensfases,<br/>één adres.
            </h2>
            <div className="space-y-6 text-lg text-secondary font-normal md:font-light leading-relaxed">
              <p>
                Acht exclusieve woningen tussen de Leopoldstraat en de Floris Primsstraat. Eén architecturaal geheel, gebouwd voor het leven.
              </p>
              <p>
                Of je nu net begint met stapelen en ruimte zoekt voor je gezin, of juist wilt ontspullen en zorgeloos wilt genieten van gelijkvloers comfort. LEO biedt de perfecte fundering.
              </p>
            </div>
          </div>
          <div className="md:col-span-7 relative isometric-hover">
            <img 
              {...getCloudinaryProps("https://res.cloudinary.com/dwoegbacd/image/upload/v1781784226/WONINGEN_-_BLUEPRINT_05_final_phkcjm.webp", [400, 800, 1200, 1600], 1200, "(max-width: 768px) 100vw, 50vw")}
              alt="Luxe en minimalistisch interieur van Project LEO in Grobbendonk door Blueprint Development"
              width={1200}
              height={900}
              className="w-full h-auto aspect-[4/3] object-cover shadow-2xl"
              referrerPolicy="no-referrer"
              loading="lazy"
              decoding="async"
            />
            {/* Overlapping typography block */}
            <div className="absolute -bottom-12 -left-12 bg-surface p-8 hidden md:block shadow-xl border-l-4 border-primary">
              <p className="font-serif text-3xl tracking-widest text-tertiary">08</p>
              <p className="text-xs tracking-[0.2em] uppercase mt-2 text-secondary">Woningen</p>
            </div>
            <dl className="mt-6 grid grid-cols-2 min-[360px]:grid-cols-[1fr_1fr_1.7fr] gap-3 border-l-4 border-primary bg-surface p-4 sm:p-5 md:hidden">
              <div>
                <dt className="text-xs tracking-[0.05em] uppercase text-secondary">Woningen</dt>
                <dd className="font-serif text-3xl text-tertiary">8</dd>
              </div>
              <div>
                <dt className="text-xs tracking-[0.05em] uppercase text-secondary">Eengezins</dt>
                <dd className="font-serif text-3xl text-tertiary">3</dd>
              </div>
              <div>
                <dt className="text-xs tracking-[0.05em] uppercase text-secondary">Appartementen</dt>
                <dd className="font-serif text-3xl text-tertiary">5</dd>
              </div>
            </dl>
          </div>
        </div>
      </section>

      <OfferSummary />

      {/* Split: Eengezinswoningen */}
      <section id="woningen" className="py-24 bg-surface-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="order-2 md:order-1 bg-surface p-12 md:p-24 flex flex-col justify-center">
              <span className="text-xs tracking-[0.2em] uppercase text-secondary mb-6 block">01 — Voor de nestbouwers</span>
              <h3 className="font-serif text-4xl md:text-5xl tracking-wide mb-8">Ruimte om te wortelen.</h3>
              <p className="text-secondary font-normal md:font-light leading-relaxed mb-8">
                Drie royale eengezinswoningen voor gezinnen die ruimte willen om te groeien.
              </p>
              <ul className="space-y-4 text-text-main mb-12">
                <li className="flex items-start gap-4">
                  <div className="w-1.5 h-1.5 bg-primary mt-2 flex-shrink-0"></div>
                  <span>Eigen tuin</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-1.5 h-1.5 bg-primary mt-2 flex-shrink-0"></div>
                  <span>Ruime indeling over meerdere verdiepingen</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-1.5 h-1.5 bg-primary mt-2 flex-shrink-0"></div>
                  <span>Solide, architecturale basis voor de toekomst</span>
                </li>
              </ul>
              <div>
                <a href="#contact" className="bg-primary text-white px-8 py-4 text-sm tracking-widest uppercase hover:bg-text-main transition-all flex items-center gap-3 isometric-hover w-fit">
                  Vraag informatie aan <ArrowRight size={16} />
                </a>
              </div>
            </div>
            <div className="order-1 md:order-2 relative overflow-hidden">
              <img 
                {...getCloudinaryProps("https://res.cloudinary.com/dwoegbacd/image/upload/v1781784227/WONINGEN_-_BLUEPRINT_02_final_qviocu.webp", [400, 800, 1200, 1600], 1200, "(max-width: 768px) 100vw, 50vw")}
                alt="Project LEO moderne nieuwbouw eengezinswoning te koop in Grobbendonk"
                width={1200}
                height={800}
                className="w-full h-full object-cover min-h-[50vh]"
                referrerPolicy="no-referrer"
                loading="lazy"
                decoding="async"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Split: Appartementen */}
      <section className="py-24 bg-surface-alt pt-0 relative overflow-hidden">
        <div className="absolute inset-0 lamellen-overlay opacity-30 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="relative overflow-hidden">
              <img 
                {...getCloudinaryProps("https://res.cloudinary.com/dwoegbacd/image/upload/v1781784226/WONINGEN_-_BLUEPRINT_04_final_mlomg4.webp", [400, 800, 1200, 1600], 1200, "(max-width: 768px) 100vw, 50vw")}
                alt="Project LEO luxe appartement en modern design interieur te Grobbendonk"
                width={1200}
                height={800}
                className="w-full h-full object-cover min-h-[50vh]"
                referrerPolicy="no-referrer"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="bg-surface p-12 md:p-24 flex flex-col justify-center">
              <span className="text-xs tracking-[0.2em] uppercase text-secondary mb-6 block">02 — Voor de levensgenieters</span>
              <h3 className="font-serif text-4xl md:text-5xl tracking-wide mb-8">Hoog en droog genieten.</h3>
              <p className="text-secondary font-normal md:font-light leading-relaxed mb-8">
                Vijf luxueuze appartementen waar comfort centraal staat. Geen onderhoudszorgen, wel maximale levenskwaliteit en riante buitenruimtes.
              </p>
              <ul className="space-y-4 text-text-main mb-12">
                <li className="flex items-start gap-4">
                  <div className="w-1.5 h-1.5 bg-primary mt-2 flex-shrink-0"></div>
                  <span>Riant leefterras</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-1.5 h-1.5 bg-primary mt-2 flex-shrink-0"></div>
                  <span>Gelijkvloers comfort met lifttoegang</span>
                </li>
                <li className="flex items-start gap-4">
                  <div className="w-1.5 h-1.5 bg-primary mt-2 flex-shrink-0"></div>
                  <span>Hoogwaardige afwerking, zonder onderhoudszorgen</span>
                </li>
              </ul>
              <div>
                <a href="#contact" className="bg-primary text-white px-8 py-4 text-sm tracking-widest uppercase hover:bg-text-main transition-all flex items-center gap-3 isometric-hover w-fit">
                  Vraag informatie aan <ArrowRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materiality / Full width render */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 lamellen-overlay opacity-20 pointer-events-none"></div>
        <div className="relative h-[70vh] w-full z-10">
          <img 
            {...getCloudinaryProps("https://res.cloudinary.com/dwoegbacd/image/upload/v1781784227/WONINGEN_-_BLUEPRINT_03_final_yb4pol.webp", [640, 1024, 1600, 2000], 1600, "100vw")} 
            alt="Architecturaal monolithisch gevel detail van Project LEO in Grobbendonk"
            width={1600}
            height={1000}
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute inset-0 bg-text-main/30 flex items-center justify-center">
            <h2 className="font-serif text-5xl md:text-8xl text-surface tracking-[0.1em] text-center px-4">
              Puur.<br/>Onversneden.
            </h2>
          </div>
        </div>
      </section>

      {/* Locatie Section */}
      <section id="locatie" className="py-32 bg-surface relative overflow-hidden">
        <div className="absolute inset-0 lamellen-overlay opacity-20 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
          <div className="mb-16 text-center">
            <span className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4 block">Locatie</span>
            <h2 className="font-serif text-4xl md:text-6xl tracking-wide mb-6">Centraal gelegen in Grobbendonk</h2>
            <p className="text-secondary font-light leading-relaxed max-w-2xl mx-auto">
              Leopoldstraat 62, 2280 Grobbendonk. Een strategische ligging met alle voorzieningen binnen handbereik en een vlotte verbinding naar Antwerpen en Hasselt.
            </p>
          </div>
          
          <div className="relative h-[500px] w-full shadow-2xl isometric-hover overflow-hidden border border-secondary/10">
            <iframe 
              title="Kaart van de locatie van Residentie LEO in Grobbendonk"
              width="100%" 
              height="100%" 
              frameBorder="0" 
              scrolling="no" 
              marginHeight={0} 
              marginWidth={0} 
              src="https://www.openstreetmap.org/export/embed.html?bbox=4.7235169%2C51.1925571%2C4.7341169%2C51.1974371&amp;layer=mapnik&amp;marker=51.1949971%2C4.7288169"
              style={{ filter: 'grayscale(0.2) contrast(1.1)' }}
              loading="lazy"
            ></iframe>
          </div>
          
          <div className="mt-12 flex justify-center">
            <a 
              href="https://www.google.com/maps/search/?api=1&query=Leopoldstraat+62,+Grobbendonk" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs tracking-[0.3em] uppercase text-primary font-semibold hover:text-text-main transition-colors flex items-center gap-2"
            >
              Open in Google Maps <ArrowRight size={14} />
            </a>
          </div>
        </div>
      </section>

      <FaqSection />

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-surface-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="order-2 md:order-1">
              <img 
                {...getCloudinaryProps("https://res.cloudinary.com/dwoegbacd/image/upload/v1781784226/WONINGEN_-_BLUEPRINT_06_final_momvvg.webp", [400, 800, 1200, 1600], 1200, "(max-width: 768px) 100vw, 50vw")}
                alt="Hoogwaardige afwerking en minimalistisch interieur detail van Project LEO woningen"
                width={1200}
                height={800}
                className="w-full h-full object-cover min-h-[60vh]"
                referrerPolicy="no-referrer"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="order-1 md:order-2 bg-white p-12 md:p-24 flex flex-col justify-center relative overflow-hidden shadow-2xl">
              <div className="absolute inset-0 lamellen-overlay opacity-5 pointer-events-none"></div>
              <div className="relative z-10">
                <div className="mb-16">
                  <span className="text-xs tracking-[0.3em] uppercase text-primary font-semibold mb-4 block">Contact</span>
                  <h2 className="font-serif text-4xl md:text-6xl tracking-wide mb-6">Klaar voor de volgende stap?</h2>
                  <p className="text-secondary font-light leading-relaxed max-w-md">
                    Laat je gegevens achter. We nemen contact met je op met de beschikbaarheid en de plannen.
                  </p>
                  <p className="mt-4 text-secondary leading-relaxed">
                    Liever mailen? Stuur een bericht naar{' '}
                    <a href={`mailto:${CONTACT_EMAIL}`} className="text-primary underline hover:text-text-main transition-colors">{CONTACT_EMAIL}</a>.
                  </p>
                </div>

                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="bg-text-main text-surface py-24 px-6 md:px-12 relative overflow-hidden border-t border-white/5">
        <div className="absolute inset-0 lamellen-overlay opacity-5 pointer-events-none"></div>
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-16 items-start">
            {/* Brand Column */}
            <div className="md:col-span-4 flex flex-col items-center md:items-start gap-8">
              <Logo />
              <p className="text-on-dark-muted text-sm font-normal leading-relaxed max-w-xs text-center md:text-left">
                Eén architecturaal geheel in het hart van Grobbendonk. Gebouwd voor het leven.
              </p>
              <a href={`mailto:${CONTACT_EMAIL}`} className="py-3 text-sm text-on-dark-muted underline hover:text-white transition-colors">
                {CONTACT_EMAIL}
              </a>
            </div>
            
            {/* Navigation & Social Column */}
            <div className="md:col-span-4 flex flex-col items-center gap-10">
              <div className="flex gap-4">
                <a href="https://www.facebook.com/profile.php?id=61591163987950" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/40 flex items-center justify-center text-on-dark-muted hover:text-white hover:border-white transition-all duration-500 group" aria-label="Volg ons op Facebook">
                  <Facebook size={18} className="group-hover:scale-110 transition-transform" />
                </a>
                <a href="https://www.instagram.com/residentieleo/" target="_blank" rel="noopener noreferrer" className="w-12 h-12 rounded-full border border-white/40 flex items-center justify-center text-on-dark-muted hover:text-white hover:border-white transition-all duration-500 group" aria-label="Volg ons op Instagram">
                  <Instagram size={18} className="group-hover:scale-110 transition-transform" />
                </a>
              </div>
              
              <nav aria-label="Footermenu" className="flex flex-wrap justify-center gap-x-6 gap-y-1 text-xs tracking-[0.3em] uppercase font-semibold text-on-dark-muted">
                <a href="#concept" className="px-2 py-3 hover:text-surface transition-colors">Concept</a>
                <a href="#aanbod" className="px-2 py-3 hover:text-surface transition-colors">Aanbod</a>
                <a href="#locatie" className="px-2 py-3 hover:text-surface transition-colors">Locatie</a>
                <a href="#faq" className="px-2 py-3 hover:text-surface transition-colors">FAQ</a>
                <a href="#contact" className="px-2 py-3 hover:text-surface transition-colors">Contact</a>
              </nav>

              <div className="flex gap-4 text-xs tracking-[0.25em] uppercase text-on-dark-muted">
                <a href="/privacy" className="px-3 py-3 hover:text-surface transition-colors">Privacy</a>
                <a href="/disclaimer" className="px-3 py-3 hover:text-surface transition-colors">Disclaimer</a>
              </div>
            </div>

            {/* Developer Column */}
            <div className="md:col-span-4 flex flex-col items-center md:items-end gap-8">
              <div className="flex flex-col items-center md:items-end gap-4">
                <span className="text-xs tracking-[0.3em] uppercase text-on-dark-muted font-bold">Ontwikkeld door</span>
                <a 
                  href="https://blueprintdevelopment.be" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex flex-col items-center md:items-end gap-2 group transition-all"
                >
                  <img 
                    src={getOptimizedAssetUrl("https://res.cloudinary.com/dwoegbacd/image/upload/q_auto/f_auto/v1775743320/Blueprint_Development_1._Entire_logo_White_igldq5.png", 300)} 
                    alt="Blueprint Development Logo"
                    width={200}
                    height={28}
                    className="h-7 w-auto opacity-70 group-hover:opacity-100 transition-all duration-500 grayscale group-hover:grayscale-0"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                    decoding="async"
                  />
                  <span className="text-xs tracking-widest text-on-dark-muted group-hover:text-white transition-all duration-500 font-normal lowercase">
                    blueprintdevelopment.be
                  </span>
                </a>
              </div>
              
              <div className="text-on-dark-muted text-xs tracking-widest text-center md:text-right space-y-2">
                <p className="font-medium">&copy; 2026, Blueprint Development.</p>
                <p>Ondernemingsnr. BE 1009.188.196</p>
                <p className="text-xs leading-relaxed max-w-[260px] ml-auto">
                  Alle beelden op deze website zijn louter illustratief en niet bindend.
                </p>
              </div>

              <button 
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="mt-4 w-11 h-11 rounded-full border border-white/40 flex items-center justify-center text-on-dark-muted hover:text-white hover:border-white transition-all group"
                aria-label="Scroll naar boven"
              >
                <ChevronUp size={16} className="group-hover:-translate-y-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </footer>
      <Suspense fallback={null}>
        {showContactModal && <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />}
      </Suspense>
      {/* Fixed mobile contact CTA, stays above the iPhone Safari address bar */}
      <div
        className="md:hidden fixed bottom-0 left-0 w-full z-40 px-4"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))', paddingTop: '1rem' }}
      >
        <button
          onClick={() => setShowContactModal(true)}
          className="w-full flex items-center justify-center gap-3 bg-primary text-white px-6 py-4 text-sm tracking-[0.25em] uppercase font-bold shadow-2xl"
        >
          Neem contact op <ArrowRight size={16} />
        </button>
      </div>
    </div>
    </MotionConfig>
  );
}
